import { Component, Input, OnInit } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import {
  CtBinaryOperator,
  CtButtonConfiguration,
  CtModelConfiguration,
  CTModelDatatableFilter,
  CtModelDatatableOperators,
  CtModelOnFlyCreateService,
  CtModelOperator,
  CtModelRouteData,
  CtModelService,
  CtModelType,
  MAT_RAISED_PRIMARY,
  SnackbarService
} from "@ctsolution/ct-framework";
import { ContactInformationService } from "./contact-information.service";
import { ContactInformationRouteData } from "../../../_core/route-data/contact-information.route-data";
import { ContactInformation } from "./contact-information";
import { CtWebapiGenericResponse, CtWebapiService, DataRequest } from "@ctsolution/ct-webapi";
import { CustomCompanySetupEnum } from '../../company/company-edit/company-edit.component';
import { CTConfigurationModel } from 'projects/ct-manager/src/app/_core/classes/ct-configuration-model';
import { CtManagerService } from '../../../_core/lib/ct-manager.service';
import { DEFAULT_DATATABLE_CONFIGURATION } from '../../../_core/constants';
import { 
  ContactDuplicateConfirmationDialogComponent,
  ContactDuplicateConfirmationResult 
} from '../contact-edit/contact-duplicate-confirmation-dialog/contact-duplicate-confirmation-dialog.component';

/**
 * Component per la gestione delle informazioni di contatto
 * (email, telefono, fax, PEC, ecc.)
 * 
 * Supporta 3 modalità di configurazione:
 * - BASE: Setup standard con datatable
 * - CUSTOM: Setup personalizzato da JSON template
 * - BOTH: Entrambi (CUSTOM + BASE)
 * 
 * Pattern utilizzato:
 * - Master-detail con masterConfiguration
 * - Configuration-driven UI tramite CTConfigurationModel
 * - Submit sincronizzato tramite parentComponent
 */
@Component({
  selector: 'app-contact-information',
  template: `
    <h5>{{ contactInformationConfiguration.label | translate }}</h5>
    <div class="contact-information-container" *ngIf="modelConfiguration">
      <mat-card>
        <mat-card-content>
          <div class="ct-model-container">
            <ct-model [configuration]="modelConfiguration"></ct-model>
          </div>
          <mat-card-actions *ngIf="modelConfiguration?.form" align="end">
            <ct-button [configuration]="submitButton"></ct-button>
          </mat-card-actions>
        </mat-card-content>
      </mat-card>
    </div>
    <div style="margin-top:20px" class="ct-model-container" *ngIf="extraModelConfiguration">
      <ct-model [configuration]="extraModelConfiguration"></ct-model>
    </div>
  `,
  styles: [`
    ::ng-deep .contact-information-container {
      .ct-model-container {
        .mat-card {
          margin: 0;
          box-shadow: none !important;
          .mat-card-content {
            padding: 0 !important;
          }
        }
      }
    }
  `]
})
export class ContactInformationComponent implements OnInit {

  /** Configurazione master del contact (contiene l'OID del contact) */
  @Input() masterConfiguration: CtModelConfiguration<any> | null = null;
  
  /** Riferimento al componente parent ContactEditComponent per submit sincronizzato */
  @Input() parentComponent: any;

  /** Configurazione principale per il form/datatable delle informazioni */
  modelConfiguration: CtModelConfiguration<any> | null = null;
  
  /** Configurazione extra per la modalità BOTH (datatable standard) */
  extraModelConfiguration: CtModelConfiguration<any> | null = null;

  /** Configurazione personalizzabile con label e modalità di setup */
  contactInformationConfiguration: CTConfigurationModel = CTConfigurationModel
    .create()
    .setLabel("Informazioni Contatto");

  /** Pulsante di submit */
  submitButton: CtButtonConfiguration = CtButtonConfiguration
    .create()
    .setLabel("CT_PAGE.CT_FORM.save")
    .setClass('ct-model-submit-button')
    .setAction(() => this.handleSubmit())
    .setMatherialOptions(MAT_RAISED_PRIMARY);

  constructor(
    private onFlyCreateHelper: CtModelOnFlyCreateService,
    private managerService: CtManagerService,
    private contactInformationService: ContactInformationService,
    private _model: CtModelService<any>,
    private _webapi: CtWebapiService,
    private _snackbar: SnackbarService,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {}

  async ngOnInit() {
    
    // Override del componente se definito nel manager
    await this.managerService.getComponentOverride(
      "contact", 
      "contactInformationConfiguration", 
      this.contactInformationConfiguration
    );

    // Verifica che sia disponibile un ID nel master per procedere con il setup
    if (this.masterConfiguration?.RouteData?.id) {

      switch (this.contactInformationConfiguration.customContactSetup) {
        case CustomCompanySetupEnum.CUSTOM:
          this.customSetup();
          break;
        case CustomCompanySetupEnum.BASE:
          this.setup();
          break;
        case CustomCompanySetupEnum.BOTH:
          this.bothSetup();
          break;
        default:
          this.setup();
          break;
      }
    }
  }

  /**
   * Setup personalizzato (CUSTOM)
   * Mostra un form inline configurato tramite JSON template
   */
  customSetup() {
    
    const configuration: CtModelConfiguration<any> = CtModelConfiguration.create()
      .setRouteData(
        CtModelRouteData.create()
          .setModelType(CtModelType.FORM)
          .setController(`${window.location.origin}/assets/demo/${this.contactInformationConfiguration.customContactTemplate}`)
      )
      .setDefaultActionsEnabled(false)
      .setCTDatatableConfiguration(DEFAULT_DATATABLE_CONFIGURATION().setScrollbarH(false));

    this.modelConfiguration = configuration;

    // Carica i dati esistenti dal backend
    const body: CtModelDatatableOperators = CtModelDatatableOperators.create().setFilters([
      CTModelDatatableFilter.create()
        .setField('Contact.Oid')
        .setValue(this.masterConfiguration?.RouteData?.id)
        .setOperatorType(CtBinaryOperator.Equal)
    ]);

    const request: DataRequest = new DataRequest()
      .setController([ContactInformationRouteData().controller!])
      .setAction('List')
      .setQueryParams({ hideSpinner: true })
      .setBody(body);

    this._webapi.post<CtModelConfiguration<any>>(request).subscribe((result: CtWebapiGenericResponse<CtModelConfiguration<any>>) => {
      this.modelConfiguration?.setDataSource(result.Result.DataSource);
      
      // Imposta i valori del form dopo aver caricato il datasource
      setTimeout(() => {
        this.setupFormValue(this.modelConfiguration?.DataSource);
      }, 100);
    });
  }

  /**
   * Setup standard (BASE)
   * Mostra una datatable con tutte le informazioni e permette edit/create tramite modal
   */
  setup() {
    
    const configuration = CtModelConfiguration.create()
      .setRouteData(ContactInformationRouteData())
      .setCTDatatableConfiguration(DEFAULT_DATATABLE_CONFIGURATION())
      .setDisableAutoQueryFilter(true)
      .setOnEdit(event => { this.onStandardEdit(event.Oid); })
      .setOnCreate(() => { this.onStandardCreate(); })
      .setFieldsCustomTemplates([])
      .setCTDatatableConfiguration(DEFAULT_DATATABLE_CONFIGURATION().setScrollbarH(false));

    configuration.initialized().subscribe(() => {
      this.modelConfiguration?.setOperations([CtModelOperator.Update, CtModelOperator.Create]);
    });

    // Filtro per contact corrente
    configuration.filterValues.addConstantFilter(
      CTModelDatatableFilter.create()
        .setField('Contact.Oid')
        .setValue(this.masterConfiguration?.RouteData?.id)
        .setOperatorType(CtBinaryOperator.Equal)
    );

    this.modelConfiguration = configuration;
  }

  /**
   * Setup combinato (BOTH)
   * Mostra sia il form custom che la datatable standard
   */
  bothSetup() {
    
    // Setup del form custom
    this.customSetup();

    // Setup della datatable standard
    const configuration = CtModelConfiguration.create()
      .setRouteData(ContactInformationRouteData())
      .setCTDatatableConfiguration(DEFAULT_DATATABLE_CONFIGURATION())
      .setDisableAutoQueryFilter(true)
      .setOnEdit(event => { this.onStandardEdit(event.Oid); })
      .setOnCreate(() => { this.onStandardCreate(); })
      .setFieldsCustomTemplates([])
      .setCTDatatableConfiguration(DEFAULT_DATATABLE_CONFIGURATION().setScrollbarH(false));

    configuration.initialized().subscribe(() => {
      this.modelConfiguration?.setOperations([CtModelOperator.Update, CtModelOperator.Create]);
    });

    configuration.filterValues.addConstantFilter(
      CTModelDatatableFilter.create()
        .setField('Contact.Oid')
        .setValue(this.masterConfiguration?.RouteData?.id)
        .setOperatorType(CtBinaryOperator.Equal)
    );

    this.extraModelConfiguration = configuration;
  }

  /**
   * Imposta i valori iniziali del form in base ai dati esistenti
   * @param value Array di ContactInformation
   */
  setupFormValue(value: Array<ContactInformation>) {
    
    const formValue = this.modelConfiguration?.form?.value;
    this.contactInformationService.getContactInformationTypes().subscribe(contactInformationTypes => {
      
      Object.keys(formValue).forEach(k => {
        
        // Trova il codice del tipo di informazione
        const codeValue = contactInformationTypes.find(
          element => element.Description?.toLowerCase() === k.toLowerCase()
        );
        
        // Trova il valore esistente per questo tipo
        const existingValue = value.find(
          element => +(element.Type ?? '0') === +(codeValue?.Code ?? '0')
        );
        
        // Se esiste, imposta il valore nel form
        if (existingValue) {
          this.modelConfiguration?.form?.get(k)?.setValue(existingValue.Content);
        }
      });
    });
  }

  /**
   * Apre il form per la modifica standard (modalità BASE o BOTH)
   * @param Oid Identificativo dell'informazione da modificare
   */
  onStandardEdit(Oid: number | null = null) {
    
    const routeData: CtModelRouteData = CtModelRouteData.create()
      .setController(ContactInformationRouteData().controller)
      .setModelType(CtModelType.FORM);

    const configuration: CtModelConfiguration<any> = CtModelConfiguration.create();
    
    if (Oid) {
      routeData.setId(Oid);
    }
    
    configuration.setRouteData(routeData)
      .setFieldsCustomTemplates([
        { prop: 'ContactOid', visible: false },  // ← Nascondi ContactOid
        { prop: 'Oid', visible: false }          // ← Nascondi Oid
      ]);

    this.onFlyCreateHelper.openOnFlyCreate(configuration)
      .afterClosed()
      .subscribe(reload => {
        if (reload) {
          // Refresh forzato
          window.location.reload();
        }
      });
  }

  /**
   * Apre il form per la creazione standard (modalità BASE o BOTH)
   */
  onStandardCreate() {
    
    const routeData: CtModelRouteData = CtModelRouteData.create()
      .setController(ContactInformationRouteData().controller)
      .setModelType(CtModelType.FORM);

    const configuration: CtModelConfiguration<any> = CtModelConfiguration.create();
    configuration.setRouteData(routeData)
      .setFieldsCustomTemplates([
        { prop: 'ContactOid', visible: false },  // ← Nascondi ContactOid
        { prop: 'Oid', visible: false }          // ← Nascondi Oid
      ]);

    // Imposta il filtro per il ContactOid
    if (this.masterConfiguration?.RouteData?.id) {
      configuration.filterValues.addConstantFilter(
        CTModelDatatableFilter.create()
          .setField('ContactOid')
          .setValue(this.masterConfiguration?.RouteData?.id)
          .setOperatorType(CtBinaryOperator.Equal)
      );
    }

    this.onFlyCreateHelper.openOnFlyCreate(configuration)
      .afterClosed()
      .subscribe(reload => {
        if (reload) {
          window.location.reload();
        }
      });
  }

  /**
   * Gestisce il click sul pulsante "Salva"
   * Se siamo in modalità edit (con parentComponent disponibile), chiama il submit sincronizzato
   * Altrimenti, esegue il submit tradizionale standalone
   */
  handleSubmit(): void {
    
    // Controlla se siamo in modalità edit verificando se esiste un id nel masterConfiguration
    const isEditMode = this.masterConfiguration?.RouteData?.id;
    
    // Se abbiamo il parent component, siamo in edit mode E il metodo submitAll esiste, usa il submit sincronizzato
    if (this.parentComponent && this.parentComponent.submitAll && isEditMode) {
      this.parentComponent.submitAll();
    } else {
      // Altrimenti usa il metodo tradizionale standalone
      this.onSubmit();
    }
  }

  /**
   * Metodo esposto per il submit sincronizzato dal parent component
   * Restituisce un Observable per essere usato con forkJoin
   * 
   * IMPORTANTE: Se in modalità CUSTOM, controlla prima i duplicati CF/PIVA
   * 
   * @returns Observable<any>
   */
  submitForm(): Observable<any> {
    
    return new Observable(observer => {
      
      // Se non c'è un form da salvare, completa immediatamente con successo
      if (!this.modelConfiguration?.form) {
        observer.next({ success: true, message: 'No form to save' });
        observer.complete();
        return;
      }

      const value = this.modelConfiguration.form.value;
      const keys = Object.keys(value);

      // Se non ci sono campi, completa con successo
      if (keys.length === 0) {
        observer.next({ success: true, message: 'No fields to save' });
        observer.complete();
        return;
      }

      // CONTROLLO DUPLICATI - Solo in modalità CUSTOM
      // Estrae FiscalCode e VAT dai valori del form per controllarli
      const fiscalCodeValue = value['FiscalCode'] || null;
      const vatValue = value['VAT'] || null;
      const currentContactOid = this.masterConfiguration?.RouteData?.id ?? null;

      // Esegue il controllo duplicati PRIMA di procedere con il salvataggio
      // Solo se almeno uno dei due campi è valorizzato
      if ((fiscalCodeValue || vatValue) && this.contactInformationConfiguration.customContactSetup === CustomCompanySetupEnum.CUSTOM) {
        
        this.checkDuplicateContact(fiscalCodeValue, vatValue, currentContactOid).then(isDuplicate => {
          
          if (isDuplicate) {
            // Se esiste un duplicato, mostra la modale di conferma
            this.showDuplicateConfirmationDialog().then(userConfirmed => {
              
              if (!userConfirmed) {
                // L'utente ha annullato, non procedo con il salvataggio
                observer.error({ canceled: true, message: 'User canceled due to duplicate' });
                return;
              }
              
              // L'utente ha confermato, procedo con il salvataggio
              this.proceedWithSubmit(value, keys, observer);
            });
          } else {
            // Non ci sono duplicati, procedo direttamente con il salvataggio
            this.proceedWithSubmit(value, keys, observer);
          }
        }).catch(error => {
          console.error('Errore durante il controllo duplicati:', error);
          // In caso di errore nel controllo, procedo comunque con il salvataggio
          this.proceedWithSubmit(value, keys, observer);
        });
      } else {
        // Nessun controllo duplicati necessario, procedo direttamente
        this.proceedWithSubmit(value, keys, observer);
      }
    });
  }

  /**
   * Esegue effettivamente il salvataggio delle ContactInformation
   * Questo metodo è stato estratto da submitForm() per permettere il controllo duplicati
   * 
   * @param value Valori del form
   * @param keys Chiavi dei campi del form
   * @param observer Observer dell'Observable per notificare il completamento
   */
  private proceedWithSubmit(value: any, keys: string[], observer: any): void {

      this.contactInformationService.getContactInformationTypes().subscribe({
        next: (contactInformationTypes) => {
          
          let completedRequests = 0;
          const totalRequests = keys.length;
          let hasError = false;

          keys.forEach((k) => {
            
            // Trova il codice del tipo di informazione
            const codeValue = contactInformationTypes.find(
              element => element.Description?.toLowerCase() === k.toLowerCase()
            );
            
            if (codeValue) {
              
              // Crea l'oggetto ContactInformation
              const contactInformation = ContactInformation.create()
                .setContactOid(this.masterConfiguration?.RouteData?.id ?? -1)
                .setContent(value[k] ?? '')
                .setType(codeValue.Code ?? null);

              const configuration: CtModelConfiguration<any> = CtModelConfiguration.create()
                .setRouteData(
                  CtModelRouteData.create()
                    .setController(ContactInformationRouteData().controller)
                );

              // Trova se esiste già un record per questo tipo
              const existingValue: ContactInformation | undefined = this.modelConfiguration?.DataSource.find(
                (element: ContactInformation) => element.Type != null && (+element.Type) === +(contactInformation.Type ?? '0')
              );

              // Se esiste, imposta l'OID per fare update
              if (existingValue) {
                configuration.RouteData?.setId(existingValue.Oid ?? -1);
              }

              // Salva l'informazione
              this._model.putInfos(configuration, contactInformation)?.subscribe({
                next: () => {
                  completedRequests++;
                  
                  // Se tutte le richieste sono completate, completa l'observable
                  if (completedRequests === totalRequests && !hasError) {
                    observer.next({ success: true, message: 'All contact information saved' });
                    observer.complete();
                  }
                },
                error: (err) => {
                  hasError = true;
                  console.error(`Errore durante il salvataggio di ${k}:`, err);
                  observer.error(err);
                }
              });
            } else {
              // Se non trova il codeValue, conta comunque come completato
              completedRequests++;
              if (completedRequests === totalRequests && !hasError) {
                observer.next({ success: true, message: 'All contact information saved' });
                observer.complete();
              }
            }
          });
        },
        error: (err) => {
          console.error('Errore durante il recupero dei tipi informazione:', err);
          observer.error(err);
        }
      });
    }
  

  /**
   * Gestisce il salvataggio del form (metodo standalone legacy)
   * Questo metodo viene usato solo quando il componente non è in una configurazione sincronizzata
   */
  onSubmit() {
    
    this.modelConfiguration?.form?.markAllAsTouched();

    if (this.modelConfiguration?.form?.valid) {
      
      const value = this.modelConfiguration.form.value;
      const keys = Object.keys(value);

      this.contactInformationService.getContactInformationTypes().subscribe(contactInformationTypes => {
        
        keys.forEach((k, idx) => {
          
          const codeValue = contactInformationTypes.find(
            element => element.Description?.toLowerCase() === k.toLowerCase()
          );
          
          if (codeValue) {
            
            const contactInformation = ContactInformation.create()
              .setContactOid(this.masterConfiguration?.RouteData?.id ?? -1)
              .setContent(value[k] ?? '')
              .setType(codeValue.Code ?? null);

            const configuration: CtModelConfiguration<any> = CtModelConfiguration.create()
              .setRouteData(
                CtModelRouteData.create()
                  .setController(ContactInformationRouteData().controller)
              );

            const existingValue: ContactInformation | undefined = this.modelConfiguration?.DataSource.find(
              (element: ContactInformation) => element.Type != null && (+element.Type) === +(contactInformation.Type ?? '0')
            );

            if (existingValue) {
              configuration.RouteData?.setId(existingValue.Oid ?? -1);
            }

            this._model.putInfos(configuration, contactInformation)?.subscribe(() => {
              
              // Se è l'ultima richiesta, mostra notifica e refresh
              if (idx === (keys.length - 1)) {
                this._snackbar.onSuccess();
                this.modelConfiguration = null;
                setTimeout(() => window.location.reload());
              }
            });
          }
        });
      });
    }
  }

  /**
   * Controlla se esiste già un contact con lo stesso CF o P.IVA
   * nelle ContactInformation (Type 19 = FiscalCode, Type 20 = VAT)
   * 
   * @param fiscalCode Codice fiscale da controllare
   * @param vat Partita IVA da controllare  
   * @param currentContactOid OID del contact corrente (per escluderlo dalla ricerca)
   * @returns Promise<boolean> True se esiste già un duplicato, false altrimenti
   */
  private async checkDuplicateContact(fiscalCode: string | null, vat: string | null, currentContactOid: number | null): Promise<boolean> {
    
    // Se entrambi i campi sono vuoti, non c'è nulla da controllare
    if (!fiscalCode && !vat) {
      return false;
    }

    // Creo la configurazione per la chiamata al model service usando il controller di ContactInformation
    const configuration: CtModelConfiguration<any> = CtModelConfiguration
      .create()
      .setRouteData(
        CtModelRouteData
          .create()
          .setController(ContactInformationRouteData().controller)
      );

    // Creo l'array di filtri per la ricerca
    const filters: CTModelDatatableFilter[] = [];

    // Se è in modalità edit, escludo il contact corrente dalla ricerca
    // Filtro: Contact.Oid != currentContactOid
    if (currentContactOid) {
      filters.push(
        CTModelDatatableFilter.create()
          .setField('Contact.Oid')
          .setValue(currentContactOid)
          .setOperatorType(CtBinaryOperator.NotEqual)
      );
    }

    // Aggiungo il filtro per il Codice Fiscale (Type 19) solo se valorizzato
    // Filtro: (Type == 19 AND Content == fiscalCode) OR ...
    const orFilters: CTModelDatatableFilter[] = [];

    if (fiscalCode && fiscalCode.trim() !== '') {
      orFilters.push(
        CTModelDatatableFilter.create()
          .setField('[Type] == 19 AND Contains([Content], ?)')
          .setValue([fiscalCode.trim()])
          .setOperatorType(CtBinaryOperator.OrOperator)
      );
    }

    // Aggiungo il filtro per la Partita IVA (Type 20) solo se valorizzata
    // Filtro: (Type == 20 AND Content == vat)
    if (vat && vat.trim() !== '') {
      orFilters.push(
        CTModelDatatableFilter.create()
          .setField('[Type] == 20 AND Contains([Content], ?)')
          .setValue([vat.trim()])
          .setOperatorType(CtBinaryOperator.OrOperator)
      );
    }

    // Se non ci sono filtri OR da applicare, non c'è nulla da controllare
    if (orFilters.length === 0) {
      return false;
    }

    // Combino i filtri
    filters.push(...orFilters);

    // Creo il body per la chiamata con i filtri
    const body: CtModelDatatableOperators = CtModelDatatableOperators
      .create()
      .setFilters(filters);

    try {
      // Eseguo la chiamata al backend per ottenere la lista delle ContactInformation
      const response: CtWebapiGenericResponse<any> = await firstValueFrom(
        this._model.getList(configuration, body)
      );

      // Se il TotalElement è maggiore di 0, vuol dire che esiste già un contact con uno dei due campi
      return (response?.Result?.TotalElement ?? 0) > 0;

    } catch (error) {
      console.error('Errore durante il controllo del duplicato contact:', error);
      // In caso di errore, per sicurezza procedo senza bloccare l'utente
      return false;
    }
  }

  /**
   * Mostra la modale di conferma per il duplicato
   * @returns Promise<boolean> True se l'utente conferma, false altrimenti
   */
  private async showDuplicateConfirmationDialog(): Promise<boolean> {
    
    const dialogRef = this.dialog.open(ContactDuplicateConfirmationDialogComponent, {
      width: '500px',
      disableClose: true // L'utente deve scegliere un'azione
    });

    const result: ContactDuplicateConfirmationResult = await firstValueFrom(
      dialogRef.afterClosed()
    );

    return result?.confirmed ?? false;
  }
}
